import React, { useState, useEffect } from "react";
import { NavLink, Outlet, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  ChartBar, UsersThree, Newspaper, Buildings, ChatCircleText,
  Gear, MagnifyingGlass, SignOut, Sun, List, X
} from "@phosphor-icons/react";
import { useAuth } from "../../context/AuthContext";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: <ChartBar size={18}/>, end: true },
  { to: "/admin/leads", label: "Leads", icon: <UsersThree size={18}/> },
  { to: "/admin/blogs", label: "Blogs", icon: <Newspaper size={18}/> },
  { to: "/admin/projects", label: "Projects", icon: <Buildings size={18}/> },
  { to: "/admin/testimonials", label: "Testimonials", icon: <ChatCircleText size={18}/> },
  { to: "/admin/content", label: "Site Content", icon: <Gear size={18}/> },
  { to: "/admin/seo", label: "SEO Settings", icon: <MagnifyingGlass size={18}/> },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace/>;

  const onLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex">
      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-[#0A1F15] text-white h-14 flex items-center justify-between px-4 shadow-md">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#FDB813] flex items-center justify-center">
            <Sun weight="fill" size={18} className="text-[#0A1F15]"/>
          </div>
          <div className="font-display font-extrabold text-sm">Orax Admin</div>
        </div>
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 rounded-lg hover:bg-white/10"
          aria-label="Open menu"
          data-testid="admin-mobile-menu-btn"
        >
          <List size={22}/>
        </button>
      </header>

      {/* Sidebar overlay (mobile) */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
          data-testid="admin-sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-50 lg:z-auto w-64 bg-[#0A1F15] text-white flex flex-col h-screen transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg bg-[#FDB813] flex items-center justify-center">
              <Sun weight="fill" size={20} className="text-[#0A1F15]"/>
            </div>
            <div>
              <div className="font-display font-extrabold text-sm">Orax Admin</div>
              <div className="text-[10px] text-white/60 uppercase tracking-wider">Dashboard</div>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded hover:bg-white/10"
            aria-label="Close menu"
          >
            <X size={20}/>
          </button>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {LINKS.map(l => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({isActive}) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition ${
                  isActive ? "bg-[#FDB813] text-[#0A1F15]" : "text-white/75 hover:bg-white/10 hover:text-white"
                }`
              }
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/\s/g,'-')}`}
            >
              {l.icon} {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/60 mb-2 px-3 truncate">{user.email}</div>
          <button onClick={onLogout} className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-white/75 hover:bg-white/10 hover:text-white" data-testid="admin-logout">
            <SignOut size={18}/> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-4 sm:p-6 md:p-10 pt-20 lg:pt-10 overflow-x-hidden min-w-0">
        <Outlet/>
      </main>
    </div>
  );
}
